import { useState } from "react";
import { useLocation } from "wouter";
import { Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { isDuplicateContactBody, toastDuplicateContact } from "@/lib/duplicateContact";
import type { RailPerson } from "./RailPeople";

/** Fill a person's blanks from Apollo (POST /api/contacts/:id/enrich-apollo). Never overwrites a value. */
export function EnrichPersonButton({ person, onDone }: { person: RailPerson; onDone: () => void }) {
  const [enriching, setEnriching] = useState(false);
  const [, navigate] = useLocation();

  const enrich = async () => {
    setEnriching(true);
    try {
      const res = await fetch(`/api/contacts/${person.id}/enrich-apollo`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" } });
      const data = await res.json();
      if (data.enriched) {
        const e = data.enriched;
        const updates: Record<string, any> = {};
        if (e.title && !person.title) updates.title = e.title;
        if (e.email && !person.email) updates.email = e.email;
        if (e.phone && !person.phone) updates.phone = e.phone;
        if (e.linkedinUrl && !person.linkedinUrl) updates.linkedinUrl = e.linkedinUrl;
        if (Object.keys(updates).length > 0) {
          const put = (u: Record<string, any>) => fetch(`/api/contacts/${person.id}`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(u) });
          let res = await put(updates);
          if (!res.ok) {
            const body = await res.json().catch(() => null);
            if (isDuplicateContactBody(body) && updates.email !== undefined) {
              // Apollo's email already belongs to someone else here: keep the rest, drop the email.
              const { email: _dropped, ...rest } = updates;
              if (Object.keys(rest).length === 0) { toastDuplicateContact(body, navigate); return; }
              res = await put(rest);
              if (res.ok) {
                onDone();
                toast.error(`${body.error} The other fields were saved.`, body.existingContactId
                  ? { action: { label: "Open", onClick: () => navigate(`/contacts/${body.existingContactId}`) } }
                  : undefined);
                return;
              }
              const retryBody = await res.json().catch(() => null);
              toast.error(retryBody?.error ?? "Couldn't save the contact");
              return;
            }
            if (isDuplicateContactBody(body)) toastDuplicateContact(body, navigate);
            else toast.error(body?.error ?? "Couldn't save the contact");
            return;
          }
          onDone();
          toast.success(`Enriched ${Object.keys(updates).length} fields`);
        } else {
          toast("No new data found from Apollo");
        }
      } else if (data.otherEmployer?.name) {
        toast(`Apollo lists ${person.name} at ${data.otherEmployer.name}, so nothing was copied. Could be a namesake, or a second business.`);
      } else {
        toast("No data found from Apollo");
      }
    } catch { toast.error("Enrichment failed"); }
    finally { setEnriching(false); }
  };

  return (
    <button
      type="button"
      onClick={enrich}
      disabled={enriching}
      aria-label={`Enrich ${person.name} from Apollo`}
      title="Fill blanks from Apollo"
      className="grid h-5 w-5 place-items-center rounded text-muted-foreground opacity-0 transition-opacity hover:text-orange-600 focus:opacity-100 group-hover:opacity-100 disabled:opacity-100"
    >
      {enriching ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
    </button>
  );
}
